/**
 * `/help` — Help → Guided tour.
 *
 * The port of the demo's Help "tour" tab (demo line 3929): a start button and
 * the 17 stops, each of which starts the tour at that stop. The tour itself
 * runs in `chrome-oem/TourBar.tsx`, a bar along the bottom of the window that
 * navigates the real pages.
 *
 * This is the Help landing. `/help` used to redirect to the platform map
 * because the tour was Tier 3. Now that the tour exists, the bare address
 * lands here, which is also where `routeForView('help', 'tour')` and "End tour"
 * point.
 *
 * @see src/versions/v1/modules/help/TourScene.transform.ts
 * @see src/versions/v1/shared/tour-engine.ts
 * @see docs/logic/help/tour.md
 */

import { useMemo } from 'react';
import { ChevronRight, Flag } from 'lucide-react';
import { HelpAnchor, SurfacePageBody, SurfacePageHeader, SurfacePageLayout } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { BUTTON_PRIMARY } from '@/shared/ui-class';
import { TOUR, TOUR_MINUTES } from '../../shared/seed/tour';
import { useTour } from '../../shared/tour-engine';
import { HelpTabs } from './HelpTabs';
import { tourRows } from './TourScene.transform';

const STANDFIRST = `${TOUR.length} stops, ${TOUR_MINUTES} — the bar stays with you as you go.`;

export function TourScene() {
  const { active, index, start, go } = useTour();
  const rows = useMemo(() => tourRows(TOUR, active, index), [active, index]);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="TourScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-6" as="div">
          <SurfacePageHeader
            kicker="Help"
            title="Guided tour"
            description={STANDFIRST}
            actions={<HelpAnchor doc="help/tour" />}
            tabs={<HelpTabs current="tour" />}
            toolbarActions={
              <button type="button" className={BUTTON_PRIMARY} onClick={start}>
                <Flag className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
                {active ? 'Restart the tour' : 'Start the tour'}
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            }
          />

          <Commentable name="TourStops">
            <ol
              aria-label="Tour stops"
              className="grid grid-cols-1 gap-2 @4xl:grid-cols-2"
            >
              {rows.map((row) => (
                <li key={row.id} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => go(row.index)}
                    aria-current={row.current ? 'step' : undefined}
                    aria-label={`Start the tour at step ${row.n}: ${row.title}`}
                    className={cn(
                      'group flex h-full w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
                      'hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]',
                    )}
                    style={{
                      borderColor: row.current ? 'var(--accent)' : 'var(--hairline)',
                      background: 'var(--surface-2)',
                    }}
                  >
                    <span
                      className="mt-0.5 w-6 shrink-0 text-[12px] font-semibold tabular-nums"
                      style={{ color: row.current ? 'var(--accent)' : 'var(--ink-tertiary)' }}
                    >
                      {row.n}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className="block text-[13px] font-semibold"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {row.title}
                      </span>
                      <span
                        className="mt-0.5 block text-[12px] leading-[1.5]"
                        style={{ color: 'var(--ink-secondary)' }}
                      >
                        {row.body}
                      </span>
                      <span
                        className="mt-1 block truncate font-mono text-[10.5px]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        {row.route}
                      </span>
                    </span>
                    <span
                      className="mt-0.5 shrink-0 whitespace-nowrap text-[11px] font-medium"
                      style={{ color: 'var(--accent)' }}
                      aria-hidden="true"
                    >
                      {row.current ? 'Here now' : 'Open ›'}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </Commentable>

          <Commentable name="TourNote">
            <p
              className="max-w-3xl text-[12.5px] leading-relaxed"
              style={{ color: 'var(--ink-secondary)' }}
            >
              The tour navigates the platform for you — the bar at the bottom carries the
              commentary and the Back / Next controls, so you can talk over it without losing
              your place. With the bar focused, ← and → step through the stops and Esc ends the
              tour. It can also be started from the account menu.
            </p>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
